import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Spinner, Badge, Modal, Pagination } from 'react-bootstrap';
import { Phone, MessageSquare, Search, TrendingUp, Globe, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { authService } from '../../services/authService';
import './ProductList.css';
const ProductList = () => {
  const [voiceProducts, setVoiceProducts] = useState([]);
  const [smsProducts, setSmsProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [productDetails, setProductDetails] = useState([]);
  const [search, setSearch] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const itemsPerPage = 30;
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  useEffect(() => {
    loadProducts();
  }, []);
  const loadProducts = async () => {
    setLoading(true);
    try {
      const [voiceResponse, smsResponse] = await Promise.all([
        api.get('/products/voice'),
        api.get('/products/sms')
      ]);
      setVoiceProducts(voiceResponse.data);
      setSmsProducts(smsResponse.data);
    } catch (error) {
      console.error('Load products error:', error);
    } finally {
      setLoading(false);
    }
  };
  const loadProductDetails = async (rateId, type, name, page = 1, letter = '', searchTerm = '', details = false) => {
    setDetailsLoading(true);
    setSelectedProduct(rateId);
    setSelectedProductType(type);
    setSelectedProductName(name);
    setCurrentPage(page);
    setSelectedLetter(letter);
    setShowModal(true);
    try {
      const offset = (page - 1) * itemsPerPage;
      const endpoint = type === 'voice' ? `/products/voice/${rateId}` : `/products/sms/${rateId}`;
      const params = {
        search: searchTerm,
        limit: itemsPerPage,
        offset: offset,
        showDetails: details ? 'true' : 'false',
        letter: letter
      };
      const response = await api.get(endpoint, { params });
      setProductDetails(response.data.data);
      const total = response.data.total;
      setTotalPages(Math.ceil(total / itemsPerPage));
    } catch (error) {
      console.error('Load product details error:', error);
    } finally {
      setDetailsLoading(false);
    }
  };
  const handleSearchChange = (value) => {
    setSearch(value);
    if (value.trim() !== '') {
      setSelectedLetter('');
    }
  };
  const handleSearch = () => {
    if (selectedProduct && selectedProductType) {
      loadProductDetails(selectedProduct, selectedProductType, selectedProductName, 1, '', search, showDetails);
    }
  };
  const handleLetterClick = (letter) => {
    const newLetter = selectedLetter === letter ? '' : letter;
    setSelectedLetter(newLetter);
    if (selectedProduct && selectedProductType) {
      loadProductDetails(selectedProduct, selectedProductType, selectedProductName, 1, newLetter, search, showDetails);
    }
  };
  const handleOffClick = () => {
    setSelectedLetter('');
    if (selectedProduct && selectedProductType) {
      loadProductDetails(selectedProduct, selectedProductType, selectedProductName, 1, '', search, showDetails);
    }
  };
  const handlePageChange = (page) => {
    if (selectedProduct && selectedProductType) {
      loadProductDetails(selectedProduct, selectedProductType, selectedProductName, page, selectedLetter, search, showDetails);
    }
  };
  const handleShowDetailsChange = (checked) => {
    setShowDetails(checked);
    if (selectedProduct && selectedProductType) {
      loadProductDetails(selectedProduct, selectedProductType, selectedProductName, 1, selectedLetter, search, checked);
    }
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setSearch('');
    setSelectedLetter('');
    setShowDetails(false);
    setCurrentPage(1);
  };
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    let items = [];
    const maxVisible = 10;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    if (startPage > 1) {
      items.push(
        <Pagination.First key="first" onClick={() => handlePageChange(1)} />
      );
      items.push(
        <Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
      );
    }
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    if (endPage < totalPages) {
      items.push(
        <Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
      );
      items.push(
        <Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} />
      );
    }
    return <Pagination className="justify-content-center mt-4">{items}</Pagination>;
  };
  const getTableColumns = () => {
    if (showDetails) {
      return (
        <tr>
          <th>Prefix</th>
          <th>Destination</th>
          <th>Rate</th>
          <th>Currency</th>
          <th>Start Date</th>
          <th>Bill Increment</th>
        </tr>
      );
    } else {
      return (
        <tr>
          <th>Destination</th>
          <th>Rate</th>
          <th>Currency</th>
          <th>Start Date</th>
        </tr>
      );
    }
  };
  const renderTableRow = (detail, idx) => {
    if (showDetails) {
      return (
        <tr key={idx}>
          <td><Badge bg="secondary">{detail.prefix}</Badge></td>
          <td>{detail.description}</td>
          <td className="rate-cell">{detail.voice_rate_sale}</td>
          <td>{detail.currency_identifier}</td>
          <td>{new Date(detail.st_date).toLocaleDateString()}</td>
          <td>{detail.bill_rule_name}</td>
        </tr>
      );
    } else {
      return (
        <tr key={idx}>
          <td>{detail.description}</td>
          <td className="rate-cell">{detail.voice_rate_sale}</td>
          <td>{detail.currency_identifier}</td>
          <td>{new Date(detail.st_date).toLocaleDateString()}</td>
        </tr>
      );
    }
  };
  return (
    <div className="product-list-wrapper">
      <div className="hero-section">
        <Container>
          <div className="hero-content text-center">
            <h1 className="hero-title">Professional Voice & SMS Solutions</h1>
            <p className="hero-subtitle">Connect globally with premium telecommunication services</p>
            {!authService.isAuthenticated() && (
              <div className="hero-cta mt-4 d-flex justify-content-center gap-3 flex-wrap">
                <Button as={Link} to="/register" variant="success" size="lg" className="fw-semibold px-4">
                  Get started — Register free
                </Button>
                <Button as={Link} to="/login" variant="outline-light" size="lg" className="px-4">
                  Login
                </Button>
              </div>
            )}
          </div>
        </Container>
      </div>
      <Container className="mt-5 mb-5">
        <div className="service-section mb-5">
          <div className="section-header voice-header">
            <div className="section-icon">
              <Phone size={32} />
            </div>
            <div>
              <h2 className="section-title">Voice Services</h2>
              <p className="section-description">
                High-quality voice termination with global coverage. Our voice products deliver crystal-clear connections, 
                competitive rates, and reliable service across 200+ countries. Perfect for VoIP providers, call centers, 
                and enterprises seeking professional telecommunication solutions.
              </p>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading products...</p>
            </div>
          ) : (
            <Row className="g-4">
            {voiceProducts.map((product) => (
              <Col
                key={product.id}
                xs={12}
                sm={voiceProducts.length === 1 ? 12 : voiceProducts.length === 2 ? 6 : 6}
                md={voiceProducts.length === 1 ? 12 : voiceProducts.length === 2 ? 6 : voiceProducts.length === 3 ? 4 : 6}
                lg={voiceProducts.length === 1 ? 12 : voiceProducts.length === 2 ? 6 : voiceProducts.length === 3 ? 4 : 6}
              >
                <Card
                  className={`product-card voice-card ${
                    selectedProduct === product.id && selectedProductType === 'voice' ? 'active' : ''
                  }`}
                  onClick={() =>
                    loadProductDetails(product.id, 'voice', product.name, 1, '', '', false)
                  }
                >
                    <Card.Body>
                      <div className="product-header-row">
                        <div className="product-icon voice-icon">
                          <Phone size={20} />
                        </div>
                        <Card.Title className="product-title-inline">{product.name}</Card.Title>
                        <div className="product-badges-inline">
                          <Badge bg="success" className="me-1">
                            <Globe size={10} />
                          </Badge>
                          <Badge bg="info">
                            <Shield size={10} />
                          </Badge>
                        </div>
                      </div>
                      {product.description && (
                        <Card.Text className="product-description">{product.description}</Card.Text>
                      )}
                      <Button variant="primary" size="sm" className="mt-3 w-100 view-details-btn">
                        View Rates <TrendingUp size={16} className="ms-2" />
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
        <div className="service-section">
          <div className="section-header sms-header">
            <div className="section-icon">
              <MessageSquare size={32} />
            </div>
            <div>
              <h2 className="section-title">SMS Services</h2>
              <p className="section-description">
                Reliable SMS delivery with worldwide reach. Our SMS products ensure instant message delivery with high success rates 
                across all networks globally. Ideal for OTP verification, notifications, marketing campaigns, and enterprise messaging 
                with direct operator connections and transparent pricing.
              </p>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <p className="mt-3">Loading products...</p>
            </div>
          ) : (
            <Row className="g-4">
              {smsProducts.map((product) => (
                <Col
                key={product.id}
                xs={12}
                sm={smsProducts.length === 1 ? 12 : smsProducts.length === 2 ? 6 : 6}
                md={smsProducts.length === 1 ? 12 : smsProducts.length === 2 ? 6 : smsProducts.length === 3 ? 4 : 6}
                lg={smsProducts.length === 1 ? 12 : smsProducts.length === 2 ? 6 : smsProducts.length === 3 ? 4 : 6}
              >
                  <Card 
                    className={`product-card sms-card ${selectedProduct === product.id && selectedProductType === 'sms' ? 'active' : ''}`}
                    onClick={() => loadProductDetails(product.id, 'sms', product.name, 1, '', '', false)}
                  >
                    <Card.Body>
                      <div className="product-header-row">
                        <div className="product-icon sms-icon">
                          <MessageSquare size={20} />
                        </div>
                        <Card.Title className="product-title-inline">{product.name}</Card.Title>
                        <div className="product-badges-inline">
                          <Badge bg="success" className="me-1">
                            <Globe size={10} />
                          </Badge>
                          <Badge bg="warning" text="dark">
                            <TrendingUp size={10} />
                          </Badge>
                        </div>
                      </div>
                      {product.description && (
                        <Card.Text className="product-description">{product.description}</Card.Text>
                      )}
                      <Button variant="success" size="sm" className="mt-3 w-100 view-details-btn">
                        View Rates <TrendingUp size={16} className="ms-2" />
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      </Container>
      <Modal show={showModal} onHide={handleCloseModal} size="xl" centered className="rates-modal">
        <Modal.Header className={selectedProductType === 'voice' ? 'voice-modal-header' : 'sms-modal-header'}>
          <div className="d-flex align-items-center w-100 justify-content-between">
            <div className="d-flex align-items-center">
              {selectedProductType === 'voice' ? <Phone size={24} className="me-2" /> : <MessageSquare size={24} className="me-2" />}
              <Modal.Title>{selectedProductName} - Rate Details</Modal.Title>
            </div>
            <div className="d-flex align-items-center gap-3">
              <Badge bg={selectedProductType === 'voice' ? 'primary' : 'success'} className="text-uppercase px-3 py-2">
                {selectedProductType}
              </Badge>
              <Button variant="link" onClick={handleCloseModal} className="close-modal-btn p-0 text-white">
                ✕
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <div className="search-box">
              <Search size={20} className="search-icon" />
              <Form.Control 
                type="text" 
                placeholder="Search by prefix or country code..." 
                value={search} 
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="search-input"
              />
              <Button variant={selectedProductType === 'voice' ? 'primary' : 'success'} onClick={handleSearch} className="search-btn">
                Search
              </Button>
            </div>
          </Form.Group>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="alphabet-filter">
              <button
                className={`letter-btn off-btn ${selectedLetter === '' ? 'active' : ''}`}
                onClick={handleOffClick}
              >
                OFF
              </button>
              {alphabet.map(letter => (
                <button
                  key={letter}
                  className={`letter-btn ${selectedLetter === letter ? 'active' : ''}`}
                  onClick={() => handleLetterClick(letter)}
                >
                  {letter}
                </button>
              ))}
              
            </div>
            <Form.Check 
              type="checkbox"
              id="show-details-check"
              label="Show more details"
              checked={showDetails}
              onChange={(e) => handleShowDetailsChange(e.target.checked)}
              className="ms-3"
            />
          </div>
          {detailsLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant={selectedProductType === 'voice' ? 'primary' : 'success'} />
              <p className="mt-3">Loading rates...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped hover className="rates-table">
                  <thead>
                    {getTableColumns()}
                  </thead>
                  <tbody>
                    {productDetails.length > 0 ? (
                      productDetails.map((detail, idx) => renderTableRow(detail, idx))
                    ) : (
                      <tr>
                        <td colSpan={showDetails ? "6" : "4"} className="text-center text-muted py-4">
                          No rates found. Try adjusting your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              {renderPagination()}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default ProductList;